import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Menu, X, ChevronDown, User, LogOut, Map } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';

const navLinks = [
  { label: 'Nos séjours', href: '/nos-sejours' },
  { label: 'Notre solution', href: '/solution' },
  { label: 'Comment ça marche', href: '/comment-ca-marche' },
  { label: 'Pour les aidants', href: '/pour-les-aidants' },
  { label: 'Nos partenaires', href: '/pour-les-partenaires' },
];

const MCareFlame = () => (
  <svg width="34" height="34" viewBox="0 0 34 34" fill="none" aria-hidden="true">
    <rect width="34" height="34" rx="10" fill="#5A3B31"/>
    <path d="M17 6C17 6 10 12 10 18.5C10 22.09 13.13 25 17 25C20.87 25 24 22.09 24 18.5C24 12 17 6 17 6Z" fill="#F47B20"/>
    <path d="M17 14C17 14 14 17 14 19.5C14 21.16 15.34 22.5 17 22.5C18.66 22.5 20 21.16 20 19.5C20 17 17 14 17 14Z" fill="#FFB343"/>
    <circle cx="17" cy="20" r="2" fill="#FBF7F1" opacity="0.6"/>
  </svg>
);

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const isActive = (href) => location.pathname === href;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const dashboardLink = user?.role === 'super_admin' || user?.role === 'admin_ops'
    ? '/admin' : user?.role === 'partner' ? '/espace-partenaire' : '/espace-aidant';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-border/40' : 'bg-white/80 backdrop-blur-sm'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group flex-shrink-0" data-testid="nav-logo">
            <MCareFlame />
            <div className="flex flex-col leading-none">
              <span className="font-heading text-[17px] font-semibold text-primary tracking-tight">M-CareLink</span>
              <span className="text-[9px] tracking-[0.18em] uppercase text-muted-foreground font-body leading-none mt-0.5">Séjours de répit adaptés</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-0.5" data-testid="nav-desktop">
            {navLinks.map(link => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 font-body ${
                  isActive(link.href)
                    ? 'text-accent bg-accent/8'
                    : 'text-foreground/70 hover:text-foreground hover:bg-muted/60'
                }`}
                data-testid={`nav-link-${link.href.slice(1)}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-2.5">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-full gap-2 font-body border-border/60 text-sm" data-testid="nav-user-menu">
                    <User className="w-3.5 h-3.5" />
                    <span className="max-w-[100px] truncate">{user.first_name}</span>
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to={dashboardLink} className="cursor-pointer" data-testid="nav-dashboard-link">
                      Mon espace
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/carte-partenaires" className="cursor-pointer flex items-center gap-2" data-testid="nav-map-link">
                      <Map className="w-3.5 h-3.5 text-accent" />
                      Carte des partenaires
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="cursor-pointer text-destructive" data-testid="nav-logout-btn">
                    <LogOut className="w-4 h-4 mr-2" /> Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/connexion" data-testid="nav-login-btn">
                  <Button variant="ghost" size="sm" className="rounded-full font-body text-sm text-foreground/70 hover:text-foreground">
                    Connexion
                  </Button>
                </Link>
                <Link to="/demander-un-sejour" data-testid="nav-cta-btn">
                  <Button size="sm" className="rounded-full font-body text-sm bg-accent hover:bg-accent/90 text-white shadow-sm px-5">
                    Demander un séjour
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="nav-mobile-toggle">
                {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 pt-12 bg-background">
              <div className="flex items-center gap-2.5 mb-8">
                <MCareFlame />
                <span className="font-heading text-lg font-semibold text-primary">M-CareLink</span>
              </div>
              <nav className="flex flex-col gap-1">
                {navLinks.map(link => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setOpen(false)}
                    className={`px-4 py-3 text-base font-medium rounded-xl transition-colors font-body ${
                      isActive(link.href) ? 'text-accent bg-accent/8' : 'text-foreground hover:bg-muted/50'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="border-t border-border my-3" />
                {user ? (
                  <>
                    <Link to={dashboardLink} onClick={() => setOpen(false)} className="px-4 py-3 text-base font-medium rounded-xl hover:bg-muted/50 font-body">
                      Mon espace
                    </Link>
                    <Link to="/carte-partenaires" onClick={() => setOpen(false)} className="px-4 py-3 text-base font-medium rounded-xl hover:bg-muted/50 font-body flex items-center gap-2" data-testid="nav-mobile-map-link">
                      <Map className="w-4 h-4 text-accent" />
                      Carte des partenaires
                    </Link>
                    <button onClick={() => { logout(); setOpen(false); }} className="px-4 py-3 text-base font-medium rounded-xl text-destructive hover:bg-destructive/5 text-left font-body">
                      Déconnexion
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/connexion" onClick={() => setOpen(false)} className="px-4 py-3 text-base font-medium rounded-xl hover:bg-muted/50 font-body">
                      Connexion
                    </Link>
                    <Link to="/demander-un-sejour" onClick={() => setOpen(false)}>
                      <Button className="w-full rounded-full mt-2 bg-accent text-white font-body">Demander un séjour</Button>
                    </Link>
                  </>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
